import { useCallback, useEffect, useState } from 'react'
import {
  adminListEmployees,
  adminListTasks,
  type AdminEmployee,
  type AdminTask,
} from '../lib/adminApi'
import AdminTasks, { type TaskPrefill } from './AdminTasks'
import AdminPeople from './AdminPeople'

interface Props {
  token: string
  meId: string | null
  /**
   * Which pane to show. The top-level Tasks/People switch now lives in App —
   * there used to be a second row of tabs in here, which meant creating a task
   * was three taps deep. One switch, not two.
   */
  pane: 'tasks' | 'people'
  /** Set by Issues → "Create a task", which also switches the view to Tasks. */
  prefill: TaskPrefill | null
  onPrefillUsed: () => void
  /** Desktop row layout for the task list — see AdminTasks. */
  compact?: boolean
  /** "Create an issue" on a to-do — App routes it to the Issues form. */
  onCreateIssue?: (task: AdminTask) => void
}

export default function AdminView({
  token,
  meId,
  pane,
  prefill,
  onPrefillUsed,
  compact = false,
  onCreateIssue,
}: Props) {
  const [tasks, setTasks] = useState<AdminTask[]>([])
  const [people, setPeople] = useState<AdminEmployee[]>([])
  const [showArchived, setShowArchived] = useState(false)
  const [loading, setLoading] = useState(true)
  const [problem, setProblem] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [t, p] = await Promise.all([
        adminListTasks(token, showArchived),
        adminListEmployees(token),
      ])
      setTasks(t)
      setPeople(p)
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [token, showArchived])

  useEffect(() => {
    void load()
  }, [load])

  if (loading) return <p className="muted">Loading…</p>

  return (
    <>
      {problem && <p className="banner">{problem}</p>}

      {pane === 'tasks' ? (
        <>
          <label className="check check--inline">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            <span className="muted">Show archived</span>
          </label>
          <AdminTasks
            token={token}
            tasks={tasks}
            people={people}
            meId={meId}
            prefill={prefill}
            onPrefillUsed={onPrefillUsed}
            onChanged={() => void load()}
            onProblem={setProblem}
            {...(onCreateIssue ? { onCreateIssue } : {})}
            compact={compact}
          />
        </>
      ) : (
        <AdminPeople
          token={token}
          people={people}
          tasks={tasks}
          meId={meId}
          onChanged={() => void load()}
          onProblem={setProblem}
        />
      )}
    </>
  )
}
